import React, { useEffect, useState } from "react";
import OnboardingContainer from "../../components/onboarding/OnboardingContainer";
import VCInviteOnboardingContainer from "../../components/onboarding/VCInviteOnboardingContainer";
import { OnboardingProvider } from "../../contexts/OnboardingContext";
import { useAuth } from "../../contexts/AuthContext";
import styles from "./OnboardingDark.module.css";

/**
 * Entry point for the onboarding flow. Wraps with provider and delegates to container.
 * 
 * Architecture:
 * - OnboardingContext provides localStorage caching for fast initial render
 * - useOnboardingFlow hook handles server API communication
 * - OnboardingContainer determines the correct step based on completed_steps validation
 * - All saves sync to both server and localStorage for consistency
 * 
 * VC Invite Flow:
 * - Detects portfolio_invite_data in localStorage
 * - Uses VCInviteOnboardingContainer which skips role selection and auto-sets "Founder"
 * - Uses SelectIntegrationsVC which shows all integrations with VC-required ones highlighted
 */
const OnboardingFlow: React.FC = () => {
  const { signOut } = useAuth();
  const [isVCInvite, setIsVCInvite] = useState(false);

  // Check if running on localhost for development features
  const isLocalhost = typeof window !== "undefined" && 
    (window.location.hostname === "localhost" || 
     window.location.hostname === "127.0.0.1" ||
     window.location.hostname === "");

  // Detect VC invite onboarding flow
  useEffect(() => {
    const vcInviteData = localStorage.getItem('portfolio_invite_data');
    if (vcInviteData) {
      try {
        const parsed = JSON.parse(vcInviteData);
        setIsVCInvite(parsed.isVCInvite === true);
      } catch (e) {
        console.error('Failed to parse VC invite data');
      }
    }
  }, []);

  useEffect(() => {
    if (typeof document !== "undefined") {
      document.documentElement.classList.add("onboarding-dark");
      document.body.classList.add("onboarding-dark");
      return () => {
        document.documentElement.classList.remove("onboarding-dark");
        document.body.classList.remove("onboarding-dark");
      };
    }
  }, []);

  const handleSignOut = async () => {
    if (window.confirm("Are you sure you want to sign out? Your onboarding progress will be saved.")) {
      await signOut();
      window.location.href = "/login";
    }
  };

  return (
    <OnboardingProvider>
      <div className={styles.page}>
        {/* Sign out button - only visible on localhost */}
        {isLocalhost && (
          <button
            onClick={handleSignOut}
            className={styles.signOutButton}
            title="Sign out and return to login"
          >
            Sign Out
          </button>
        )}
        <div className={styles.glowDots} aria-hidden="true">
          <div
            className={`${styles.dot} ${styles.dotBlue}`}
            style={{ top: "8%", left: "12%" }}
          />
          <div
            className={`${styles.dot} ${styles.dotPurple}`}
            style={{ top: "20%", right: "10%" }}
          />
          <div
            className={`${styles.dot} ${styles.dotYellow}`}
            style={{ bottom: "18%", left: "22%" }}
          />
          <div
            className={`${styles.dot} ${styles.dotRed}`}
            style={{ bottom: "10%", right: "18%" }}
          />
          <div
            className={`${styles.dot} ${styles.dotTeal}`}
            style={{ top: "40%", left: "8%" }}
          />
          <div
            className={`${styles.dot} ${styles.dotPink}`}
            style={{ top: "65%", right: "8%" }}
          />
          <div
            className={`${styles.dot} ${styles.dotYellow}`}
            style={{ top: "32%", left: "45%" }}
          />
          <div
            className={`${styles.dot} ${styles.dotPurple}`}
            style={{ bottom: "28%", right: "38%" }}
          />
        </div>
        <div className={styles.content}>
          {isVCInvite ? (
            <VCInviteOnboardingContainer />
          ) : (
            <OnboardingContainer />
          )}
        </div>
      </div>
    </OnboardingProvider>
  );
};

export default OnboardingFlow;
